/**
 * Fiabilise la lecture automatique des vidéos décoratives, en particulier sur
 * Safari iOS : la propriété `muted` doit être posée sur l'élément AVANT l'appel
 * à play(), et un nouvel essai est nécessaire quand la vidéo n'est pas encore
 * décodée (ou quand iOS a refusé le premier essai, par ex. en mode économie
 * d'énergie : la lecture repart alors à la première interaction).
 */
export function attachAutoplay(video: HTMLVideoElement | null) {
  if (!video) return;

  video.autoplay = true;
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.controls = false;
  video.setAttribute("muted", "");
  video.setAttribute("autoplay", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.removeAttribute("controls");

  const tryPlay = () => {
    if (!video.paused) return;
    video.muted = true;
    const p = video.play();
    if (p && typeof p.then === "function") {
      p.catch(() => {
        /* nouvel essai au prochain événement */
      });
    }
  };

  tryPlay();
  video.addEventListener("loadedmetadata", tryPlay);
  video.addEventListener("loadeddata", tryPlay);
  video.addEventListener("canplay", tryPlay);
  window.addEventListener("pageshow", tryPlay);
  document.addEventListener("visibilitychange", tryPlay);
  document.addEventListener("touchstart", tryPlay, { passive: true, once: true });
  document.addEventListener("click", tryPlay, { once: true });

  // Safari ne démarre pas toujours une vidéo montée hors écran. On relance donc
  // la lecture au moment précis où elle entre dans la zone visible.
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting) tryPlay();
    },
    { threshold: 0.01 },
  );
  observer.observe(video);

  return () => {
    observer.disconnect();
    video.removeEventListener("loadedmetadata", tryPlay);
    video.removeEventListener("loadeddata", tryPlay);
    video.removeEventListener("canplay", tryPlay);
    window.removeEventListener("pageshow", tryPlay);
    document.removeEventListener("visibilitychange", tryPlay);
    document.removeEventListener("touchstart", tryPlay);
    document.removeEventListener("click", tryPlay);
  };
}
